import type { ReactNode } from "react";
import type { DataElScope } from "@/utils/dataEl";

export enum CtooltipPlacement {
  Above = "above",
  Below = "below",
}

export interface CtooltipProps {
  /** The component rendering this tooltip, e.g. "Cteamtags/CteamtagsTooltip".
   *  Required: it is what makes an inspected node say WHICH tooltip it belongs to. */
  dataElScope: DataElScope;
  /** Controlled visibility. The component renders nothing when false. */
  visible: boolean;
  /** DOM element the tooltip positions itself relative to. Pass via a
   *  `useState<HTMLElement | null>` setter as a ref-callback so the
   *  component re-positions when the anchor changes. Null = no overlay. */
  anchorElement: HTMLElement | null;
  /** Optional bold accent line. */
  title?: string;
  /** Optional body text or richer node below the title. */
  body?: ReactNode;
  /** Called when the user dismisses via click-outside or Esc, or when
   *  the auto-dismiss timer fires. Required to enable any of those
   *  bindings — without it the tooltip stays open until `visible`
   *  flips back to false from the parent. */
  onDismiss?: () => void;
  /** When set together with `onDismiss`, the tooltip closes itself
   *  after this many ms. Hovering pauses the timer (see
   *  `pauseAutoDismissOnHover`). */
  autoDismissMs?: number;
  /** Default true — hovering the tooltip pauses the auto-dismiss timer. */
  pauseAutoDismissOnHover?: boolean;
  /** Where the tooltip sits relative to the anchor. Default `Below`. */
  placement?: CtooltipPlacement;
  /** Fixed tooltip width in px (clamped down to viewport interior).
   *  When undefined, the tooltip fits its content. */
  width?: number;
}

export interface TooltipPosition {
  /** Pre-clamped `left` for fixed-width mode, or the anchor's
   *  horizontal centre for content-fit mode (centred via `translateX(-50%)`). */
  leftOrCenterX: number;
  /** `bottom` of the anchor for `Below`, `top` of the anchor for `Above`. */
  anchorEdgeY: number;
  /** When set, the tooltip uses this fixed width; otherwise content-fit. */
  fixedWidthPx: number | null;
}
